import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Home,
  Fuel,
  Route,
  Target,
  LogOut,
} from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";

const TABS = [
  { to: "/driver", label: "Home", icon: Home, end: true },
  { to: "/driver/refills", label: "Fuel", icon: Fuel },
  { to: "/driver/trips", label: "Trips", icon: Route },
  { to: "/driver/mileage", label: "Mileage", icon: Target },
];

function DriverInner({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "D").slice(0, 2).toUpperCase();
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="driver-shell">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white px-4 py-3 shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shadow-inner overflow-hidden p-1">
            <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-bravura-200">Bravura Trucking</div>
            <div className="text-sm font-semibold">{user?.name || user?.email || "Driver"}</div>
          </div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          data-testid="driver-logout-btn"
          title="Logout"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 pt-4 pb-24 max-w-2xl w-full mx-auto">
        {children}
      </main>

      {/* PWA Install prompt */}
      <InstallPrompt />

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
        <div className="max-w-2xl mx-auto grid grid-cols-4">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-1 text-[11px] font-medium transition ${
                  isActive ? "text-bravura" : "text-slate-500"
                }`
              }
              data-testid={`driver-tab-${t.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`h-9 w-9 rounded-xl flex items-center justify-center transition ${
                      isActive ? "bg-bravura-100 ring-2 ring-bravura-300" : "bg-transparent"
                    }`}
                  >
                    <t.icon className="h-5 w-5" />
                  </div>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function DriverShell({ children }) {
  return (
    <ProtectedRoute>
      <DriverInner>{children}</DriverInner>
    </ProtectedRoute>
  );
}
